import React, { useEffect, useState } from 'react';
import { useDarkMode } from '../../../../contexts/DarkModeContext';
import { apiPost } from '../../../../utils/api';

const StarRating = ({ rating, onRatingChange, isDarkMode }) => {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => onRatingChange(star)}
          className={`w-8 h-8 transition-colors ${
            star <= rating 
              ? 'text-yellow-400' 
              : isDarkMode ? 'text-gray-600' : 'text-gray-300'
          }`}
        >
          <svg className="w-full h-full" viewBox="0 0 20 20" fill="currentColor">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
          </svg>
        </button>
      ))}
    </div>
  );
};

const ReviewSubmissionModal = ({ 
  isOpen, 
  onClose, 
  order, 
  conversation,
  user 
}) => {
  const { isDarkMode } = useDarkMode();
  const [activeTab, setActiveTab] = useState('escrow');
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [hasSubmittedEscrow, setHasSubmittedEscrow] = useState(false);
  const [hasSubmittedSeller, setHasSubmittedSeller] = useState(false);

  // Determine if current user is the seller
  const isSeller = (() => {
    try {
      const sellerEmail = (conversation?.seller?.email || '').toLowerCase();
      const myEmail = (user?.email || '').toLowerCase();
      const sellerUid = conversation?.seller?.uid || conversation?.seller?.id;
      const myUid = user?.uid || user?.id;
      return (sellerEmail && myEmail && sellerEmail === myEmail) ||
             (sellerUid && myUid && String(sellerUid) === String(myUid));
    } catch (_) { return false; }
  })();
  const counterpartyType = isSeller ? 'buyer' : 'seller';

  // Check if this user already submitted reviews for this order to toggle button label
  useEffect(() => {
    if (!order?.order_id) return;
    let ignore = false;
    (async () => {
      try {
        const res = await fetch(`/api/reviews/order/${encodeURIComponent(order.order_id)}`, { credentials: 'include' });
        if (!ignore && res.ok) {
          const data = await res.json();
          const existing = Array.isArray(data.reviews) ? data.reviews : [];
          setHasSubmittedEscrow(existing.some(r => r.type === 'escrow'));
          setHasSubmittedSeller(existing.some(r => r.type === counterpartyType));

          // Prefill rating and text to allow editing
          const escrowExisting = existing.find(r => r.type === 'escrow');
          if (escrowExisting) {
            if (typeof escrowExisting.rating === 'number') setEscrowRating(Math.max(1, Math.min(5, escrowExisting.rating)));
            if (escrowExisting.text) setEscrowText(escrowExisting.text);
          }
          const sellerExisting = existing.find(r => r.type === counterpartyType);
          if (sellerExisting) {
            if (typeof sellerExisting.rating === 'number') setSellerRating(Math.max(1, Math.min(5, sellerExisting.rating)));
            if (sellerExisting.text) setSellerText(sellerExisting.text);
          }
        }
      } catch (_) {}
    })();
    return () => { ignore = true; };
  }, [order?.order_id, counterpartyType]);

  // Escrow review state
  const [escrowRating, setEscrowRating] = useState(5);
  const [escrowText, setEscrowText] = useState('');

  // Seller review state
  const [sellerRating, setSellerRating] = useState(5);
  const [sellerText, setSellerText] = useState('');

  const handleSubmit = async () => {
    if (!order || !conversation || !user) return;
    
    
    setSubmitting(true);
    setSubmitError('');

    try {
      const reviews = [];

      // Submit escrow review
      if (activeTab === 'escrow' && escrowText.trim()) {
        const escrowReview = {
          type: 'escrow',
          order_id: order.order_id,
          buyer_id: user.id,
          buyer_email: user.email,
          buyer_name: user.username || user.email.split('@')[0],
          rating: escrowRating,
          text: escrowText.trim(),
          platform: order.product_title ? order.product_title.split(' ')[0] : 'Unknown Platform',
          product: order.product_title || 'Unknown Product',
          amount: order.total,
          date: new Date().toISOString()
        };
        
        // Validate required fields
        if (!escrowReview.order_id || !escrowReview.buyer_id || !escrowReview.buyer_email || !escrowReview.buyer_name || !escrowReview.rating || !escrowReview.text || !escrowReview.platform || escrowReview.amount === undefined) {
          setSubmitError('Missing required review data. Please try again.');
          return;
        }
        
        reviews.push(escrowReview);
      }

      // Submit counterparty review (seller reviews buyer OR buyer reviews seller)
      if (activeTab === 'seller' && sellerText.trim()) {
        const sellerReview = (() => {
          if (counterpartyType === 'seller') {
            return {
              type: 'seller',
              order_id: order.order_id,
              buyer_id: user.id,
              buyer_email: user.email,
              buyer_name: user.username || user.email.split('@')[0],
              seller_id: conversation.seller?.uid || conversation.seller_id || conversation.seller?.id || order.seller_id,
              seller_email: conversation.seller?.email || order.seller_email,
              seller_name: conversation.seller?.name || order.seller_name,
              rating: sellerRating,
              text: sellerText.trim(),
              platform: order.product_title ? order.product_title.split(' ')[0] : 'Unknown Platform',
              product: order.product_title || 'Unknown Product',
              amount: order.total,
              date: new Date().toISOString()
            };
          }
          // Seller reviewing buyer
          return {
            type: 'buyer',
            order_id: order.order_id,
            buyer_id: user.id,
            buyer_email: user.email,
            buyer_name: user.username || user.email.split('@')[0],
            target_buyer_id: conversation.buyer?.uid || conversation.buyer?.id || conversation.buyer_id,
            target_buyer_email: conversation.buyer?.email,
            target_buyer_name: conversation.buyer?.name || conversation.buyer_username,
            rating: sellerRating,
            text: sellerText.trim(),
            platform: order.product_title ? order.product_title.split(' ')[0] : 'Unknown Platform',
            product: order.product_title || 'Unknown Product',
            amount: order.total,
            date: new Date().toISOString()
          };
        })();
        
        // Validate required fields
        if (counterpartyType === 'seller') {
          if (!sellerReview.seller_id || !sellerReview.seller_email || !sellerReview.seller_name) {
            setSubmitError('Missing seller information. Cannot submit seller review.');
            return;
          }
        } else {
          if (!sellerReview.target_buyer_id || !sellerReview.target_buyer_email || !sellerReview.target_buyer_name) {
            setSubmitError('Missing buyer information. Cannot submit buyer review.');
            return;
          }
        }
        
        reviews.push(sellerReview);
      }

      if (reviews.length === 0) {
        setSubmitError('Please write a review before submitting.');
        return;
      }

      // Submit all reviews
      const promises = reviews.map(review => {
        return apiPost('/api/reviews', review);
      });
      await Promise.all(promises);

      // Mark the current tab as submitted; keep modal open so the user can change (edit) review
      if (activeTab === 'escrow') setHasSubmittedEscrow(true);
      if (activeTab === 'seller') setHasSubmittedSeller(true);

      setSubmitSuccess(true);
      setTimeout(() => setSubmitSuccess(false), 1500);

    } catch (error) {
      setSubmitError('Failed to submit review. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className={`w-full max-w-2xl mx-4 rounded-2xl shadow-2xl ${
        isDarkMode ? 'bg-[#1a1a1a] border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Submit Review
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab('escrow')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'escrow'
                ? (isDarkMode ? 'bg-blue-900/50 text-blue-200 border-b-2 border-blue-500' : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500')
                : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
            }`}
          >
            Escrow Service Review
          </button>
          <button
            onClick={() => setActiveTab('seller')}
            className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
              activeTab === 'seller'
                ? (isDarkMode ? 'bg-blue-900/50 text-blue-200 border-b-2 border-blue-500' : 'bg-blue-50 text-blue-700 border-b-2 border-blue-500')
                : (isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
            }`}
          >
            {isSeller ? 'Buyer Review' : 'Seller Review'}
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'escrow' ? (
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Rate our Escrow Service
                </label>
                <StarRating 
                  rating={escrowRating} 
                  onRatingChange={setEscrowRating}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Your Review
                </label>
                <textarea
                  value={escrowText}
                  onChange={(e) => setEscrowText(e.target.value)}
                  placeholder="How was your experience with our escrow service?"
                  className={`w-full h-32 px-4 py-3 rounded-lg border transition-colors resize-none ${
                    isDarkMode 
                      ? 'bg-[#121212] border-gray-700 text-gray-200 placeholder-gray-500' 
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  {isSeller ? 'Rate the Buyer' : 'Rate the Seller'}
                </label>
                <StarRating 
                  rating={sellerRating} 
                  onRatingChange={setSellerRating}
                  isDarkMode={isDarkMode}
                />
              </div>
              <div>
                <label className={`block text-sm font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                  Your Review
                </label>
                <textarea
                  value={sellerText}
                  onChange={(e) => setSellerText(e.target.value)}
                  placeholder={isSeller ? 'How was your experience with this buyer?' : 'How was your experience with this seller?'}
                  className={`w-full h-32 px-4 py-3 rounded-lg border transition-colors resize-none ${
                    isDarkMode 
                      ? 'bg-[#121212] border-gray-700 text-gray-200 placeholder-gray-500' 
                      : 'bg-white border-gray-200 text-gray-800 placeholder-gray-400'
                  }`}
                />
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex items-center justify-between mt-6">
            <div className="flex-1">
              {submitSuccess && (
                <span className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                  Review submitted successfully!
                </span>
              )}
              {submitError && (
                <span className={`text-sm ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                  {submitError}
                </span>
              )}
            </div>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || (activeTab === 'escrow' ? !escrowText.trim() : !sellerText.trim())}
                className={`px-6 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  isDarkMode 
                    ? 'bg-blue-600 hover:bg-blue-500 text-white disabled:bg-gray-700 disabled:text-gray-400' 
                    : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500'
                }`}
              >
                {submitting
                  ? 'Submitting...'
                  : (activeTab === 'escrow'
                      ? (hasSubmittedEscrow ? 'Change Review' : 'Submit Review')
                      : (hasSubmittedSeller ? 'Change Review' : 'Submit Review'))}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewSubmissionModal;
